import React, { isValidElement } from 'react';
import clsx from 'clsx';
import { AriaToggleButtonProps, useToggleButton } from '@react-aria/button';
import { FocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { useFocusableRef } from '@react-spectrum/utils';
import { FocusableRef, PressEvents } from '@react-types/shared';
import { useToggleState } from '@react-stately/toggle';
import styles from '@danske/sapphire-css/components/iconButton/iconButton.module.css';

import {
  SapphireStyleProps,
  usePreventTouchEnd,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { Icon } from '../..';
import {
  AriaLabelingPropsKeys,
  AriaTogglePropsEssentialKeys,
  FocusablePropsKeys,
  GlobalDomAttributes,
  PressEventsKeys,
} from '../../utils/types';

export type ToggleIconButtonProps = Pick<
  AriaToggleButtonProps,
  | 'children'
  | 'isDisabled'
  | PressEventsKeys
  | FocusablePropsKeys
  | 'id'
  | AriaLabelingPropsKeys
  | AriaTogglePropsEssentialKeys
> &
  SapphireStyleProps &
  GlobalDomAttributes &
  PressEvents & {
    /**
     * @default 'md'
     */
    size?: 'lg' | 'md' | 'sm';
    onChange?: (selected: boolean) => void;
    isSelected?: boolean;
    defaultSelected?: boolean;
    /**
     * @default 'primary'
     */
    variant?: 'primary' | 'secondary';
  } & (
    | {
        /**
         * The button's label. This is not shown on the screen but it is required
         * for accessibility reasons since only an Icon is shown.
         */
        'aria-label': string;
        'aria-hidden'?: never;
      }
    | {
        'aria-hidden': true | 'true';
        'aria-label'?: never;
      }
  );
/**
 * Must have an `icon`, but behaves like a ToggleButton. By default, the selection is toggled when the
 * button is pressed, allowing user to switch between two states or modes.
 */
export const ToggleIconButton = React.forwardRef(function Button(
  { size = 'md', variant = 'primary', ...otherProps }: ToggleIconButtonProps,
  ref: FocusableRef<HTMLButtonElement>
) {
  useThemeCheck();

  const { autoFocus, isDisabled } = otherProps;
  const domRef = useFocusableRef<HTMLButtonElement>(ref);
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { styleProps } = useSapphireStyleProps(otherProps);

  const state = useToggleState(otherProps);
  const { buttonProps, isPressed } = useToggleButton(otherProps, state, domRef);
  usePreventTouchEnd(domRef);

  return (
    <FocusRing focusRingClass={clsx(styles['is-focus'])} autoFocus={autoFocus}>
      <button
        {...mergeProps(buttonProps, hoverProps)}
        ref={domRef}
        style={{ ...styleProps.style }}
        className={clsx(
          styles['sapphire-icon-button'],
          styles['js-focus'],
          styles['js-hover'],
          {
            [styles['is-active']]: isPressed,
            [styles['is-hover']]: isHovered,
            [styles['is-disabled']]: isDisabled,
            [styles['sapphire-icon-button--tertiary']]: variant === 'secondary',
            [styles['sapphire-icon-button--sm']]: size === 'sm',
            [styles['sapphire-icon-button--lg']]: size === 'lg',
          },
          state.isSelected
            ? styles['sapphire-icon-button--selected']
            : styles['sapphire-icon-button--unselected'],
          styleProps.className
        )}
      >
        <span
          style={{ pointerEvents: 'none' }}
          className={clsx(styles['sapphire-icon-button__icon'])}
        >
          {isValidElement(otherProps.children) ? (
            <Icon size={size === 'lg' ? 'lg' : size === 'md' ? 'md' : 'sm'}>
              {otherProps.children}
            </Icon>
          ) : (
            otherProps.children
          )}
        </span>
      </button>
    </FocusRing>
  );
});
